/* eslint-disable @typescript-eslint/no-explicit-any */
import { Checkbox, Tonality } from '@dsny/dsny-component-library';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import theme from 'src/styles/theme';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/app/store';
import {
  CheckboxStyles,
  ComboboxOptionWrapper,
  CreateArtistWrapper,
  DeleteIconWrapper,
  EnteredArtist,
  InlineErrorMessage,
  ItemWrapper,
  NoResultMessage,
  RowItemWrapper,
} from './ArtistSelection.styles';
import ArtistTag from '../ArtistTag/ArtistTag';
import { AddTrackObj, Artist, updateArtists } from '../MultiAsset.slice';

export interface ComboValueProps {
  description: string;
  subdescription?: string;
  id: string;
}

export interface ArtistProps {
  code: string;
  isChecked: string;
  isDisabled: boolean;
  isDeleteAvailable?: boolean;
  name: string;
}

export interface ArtistSelectionProps {
  tracks: AddTrackObj[];
  handleArtist: (artists: Artist[], clearSelectedArtists?: boolean) => void;
}

const checkValue = 'checkValue';
const checkValueLimit = 5;

const ArtistSelection: React.FC<ArtistSelectionProps> = ({
  tracks,
  handleArtist,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState({ id: 0, description: '' } as any);
  const [stateArtist, setStateArtist] = useState<Artist[]>([]);
  const [stateArtistSearch, setStateArtistSearch] = useState<Artist[]>([]);
  const [selectedArtists, setSelectedArtists] = useState<ArtistProps[]>();
  const [validationError, setValidationError] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>(
    t('MULTI_ASSET_ARTIST_PLACEHOLDER')
  );

  const dispatch = useDispatch<AppDispatch>();

  const { artists: artistListResponse } = useSelector(
    (state: RootState) => state.multiAsset
  );

  // Handling tracks with different artist names
  const doTracksHaveDifferentArtistNames = (
    trackList: AddTrackObj[]
  ): boolean => {
    const selectedArtistLists = trackList
      .filter((track) => !!track.isSelected)
      .map((track) => track.artists.map((a) => a.name.trim()).sort());

    if (selectedArtistLists.length <= 1) return false;

    const [firstList, ...restLists] = selectedArtistLists;

    return restLists.some(
      (list) =>
        list.length !== firstList.length ||
        list.some((name, idx) => name !== firstList[idx])
    );
  };

  // Handling findArtists
  const findArtists = (searchValue: string) => {
    const stateArtistSearchSearchTemp = stateArtist.filter((a) =>
      a.name.toLowerCase().includes(searchValue.toLowerCase())
    );
    setStateArtistSearch(stateArtistSearchSearchTemp);
  };

  // Handling findArtistsCountByName
  const findArtistCountByName = (searchValue: string) => {
    return stateArtist.filter(
      (a) => a.name.toLowerCase() === searchValue.toLowerCase()
    ).length;
  };

  // Handling sortListByChecked
  const sortListByChecked = (artistsList: ArtistProps[]) => {
    return artistsList.sort((a: ArtistProps, b: ArtistProps) => {
      if (a.isChecked && !b.isChecked) {
        return -1; // a comes before b
      }
      if (!a.isChecked && b.isChecked) {
        return 1; // b comes before a
      }
      return a.name.localeCompare(b.name);
    });
  };

  // Handling update artist item
  const updateArtistItem = (artistList: ArtistProps[]) => {
    const formattedArtists: Artist[] = artistList.map((a) => ({
      code: a.code,
      name: a.name,
      isChecked: a?.isChecked || '',
      isDisabled: a?.isDisabled || false,
      isDeleteAvailable: a?.isDeleteAvailable || false,
    }));
    handleArtist(formattedArtists);
  };

  // Handling update selection
  const updateSelection = (artistList: ArtistProps[]) => {
    setStateArtistSearch(sortListByChecked(artistList));
    setStateArtist(artistList);
    dispatch(updateArtists(artistList));
    setValue({ id: 0, description: '' } as any);
    setSelectedArtists(artistList.filter((a) => a.isChecked));
  };

  // Handling artist selection
  const onChange = (text: ComboValueProps) => {
    const tempSelectedArtists = selectedArtists || [];
    if (
      tempSelectedArtists.length < 5 &&
      !doTracksHaveDifferentArtistNames(tracks)
    ) {
      const textValue = typeof text === 'string' ? text : text.description;
      setValue({ description: textValue });
      findArtists(textValue);
    }
  };

  const openCombobox = () => {
    setOpen(true);
  };

  const closeCombobox = () => {
    setOpen(false);
  };

  // Handling disable checkbox
  const dislabledCheckBox = (artistList: ArtistProps[]) => {
    const checkedCount = artistList.filter(
      (g) => g.isChecked === checkValue
    ).length;

    return artistList.map((g) => {
      const isDisabled = !!(
        checkedCount >= checkValueLimit && g.isChecked !== checkValue
      );
      return { ...g, isDisabled };
    });
  };

  // Handling checkbox
  const handleCheckbox = (valueId: string) => {
    const matchedArtist = stateArtist.find((g) => g?.name === valueId);
    if (matchedArtist?.isDisabled) {
      return;
    }
    const newArtists = stateArtist.map((g) => {
      if (g.name === valueId && !g.isDisabled) {
        return { ...g, isChecked: g.isChecked ? '' : checkValue };
      }
      return g;
    });

    const updatedNewArtists = dislabledCheckBox(newArtists);

    updateSelection(updatedNewArtists);
  };

  // Handling onChangeSelection
  const onChangeSelection = (text: string) => {
    handleCheckbox(text);
  };

  useEffect(() => {
    if (selectedArtists) {
      updateArtistItem(selectedArtists);
      if (selectedArtists.length >= 5) {
        setSearchText(t('MULTI_ASSET_ARTIST_MAX_PLACEHOLDER'));
      } else {
        setSearchText(t('MULTI_ASSET_ARTIST_PLACEHOLDER'));
      }
    }
  }, [selectedArtists]);

  useEffect(() => {
    if (artistListResponse.items && !stateArtist.length) {
      setStateArtist(artistListResponse.items);
      setStateArtistSearch(artistListResponse.items);
    }
  }, [artistListResponse]);

  useEffect(() => {
    openCombobox();
  }, [selectedArtists]);

  useEffect(() => {
    closeCombobox();
  }, []);

  // Handling create new artist
  const handleCreateNewArtist = () => {
    const newArtist = {
      code: uuidv4(),
      name: value.description,
      isChecked: 'checkValue',
      isDisabled: false,
      isDeleteAvailable: true,
    };

    const artistTemp = [...stateArtist];
    artistTemp.push(newArtist);

    const updatedArtistTemp = dislabledCheckBox(artistTemp);

    updateSelection(updatedArtistTemp);
  };

  // Get create artist
  const getCreateArtist = () => {
    return (
      value.description && (
        <CreateArtistWrapper onClick={handleCreateNewArtist}>
          <EnteredArtist>&quot;{value.description}&quot;</EnteredArtist>
          {t('MULTI_ASSET_CREATE_ARTIST')}
        </CreateArtistWrapper>
      )
    );
  };

  // Handling close combobox
  const closeComboAfterHandle = () => {
    setTimeout(() => {
      closeCombobox();
    });
  };

  // Handling remove artist item
  const handleTabRemove = (artistName: string) => {
    onChangeSelection(artistName);
    closeComboAfterHandle();
  };

  // Handling remove artist item
  const handleArtistRemove = (artistName: string) => {
    const artistTemp = [...stateArtist];
    const index = artistTemp.findIndex((a) => a.name === artistName);
    if (index !== -1) {
      artistTemp.splice(index, 1);
    }
    const updatedArtistTemp = dislabledCheckBox(artistTemp);
    updateSelection(updatedArtistTemp);
  };

  // Handling remove multi tags
  const handleMultiTagRemove = () => {
    handleArtist([], true);
    const artistTemp = stateArtist.map((a: ArtistProps) => ({
      ...a,
      isChecked: '',
    }));
    updateSelection(artistTemp);
    closeComboAfterHandle();
  };

  useEffect(() => {
    const selectedTracks = tracks.filter(
      (track) => !!track.isSelected || track.isActive
    );

    if (selectedTracks.length === 1 && stateArtist.length) {
      const artistNames = selectedTracks[0].artists.map((a) => a.name.trim());

      const updatedArtists = stateArtist.map((a) => ({
        ...a,
        isChecked: artistNames.includes(a.name.trim()) ? 'checkValue' : '',
      }));

      const noArtistChecked = updatedArtists.every(
        (a) => a.isChecked !== 'checkValue'
      );

      const formValidationError = selectedTracks.every(
        (track) => track.isFileUploadError
      );
      setValidationError(noArtistChecked && formValidationError);
      setStateArtistSearch(sortListByChecked(updatedArtists));
      setStateArtist(updatedArtists);
    }
  }, [tracks]);

  // Handling error validation
  const getBackGroundColor = () => {
    if (validationError) {
      return theme.colors.error60;
    }
    if (doTracksHaveDifferentArtistNames(tracks)) {
      return theme.colors.neutralW70;
    }
    return '';
  };

  return (
    <>
      {/* Artist selection with seacrh */}
      <Tonality.Combobox
        setting="mtr"
        isOpen={open}
        onClick={
          doTracksHaveDifferentArtistNames(tracks) ? () => '' : openCombobox
        }
        onClose={closeCombobox}
        onChange={onChangeSelection}
        label={t('MULTI_ASSET_ARTIST_LABEL')}
        style={{
          width: '100%',
        }}
        value={value as any}
      >
        <Tonality.ComboboxSearchInput
          required
          tooltip={t('MULTI_ASSET_ARTIST_TOOLTIP')}
          placeholder={searchText}
          onChange={onChange}
          value={value.description}
          style={{
            height: '44px',
            color: theme.colors.neutralW40,
            borderColor: getBackGroundColor(),
          }}
          labelStyle={{
            marginLeft: '0px',
            color: getBackGroundColor(),
          }}
        />

        {stateArtistSearch && (
          <Tonality.ComboboxPopover
            style={{
              width: '100%',
              height: '228px',
            }}
          >
            {stateArtistSearch.length > 0 ? (
              <Tonality.ComboboxList
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                }}
              >
                {/* Create artist */}
                {findArtistCountByName(value.description) === 0 &&
                  getCreateArtist()}

                {stateArtistSearch.map((result) => (
                  <ComboboxOptionWrapper
                    key={result.code}
                    value={result.name}
                    selectedItemIndex={value.id}
                  >
                    <RowItemWrapper>
                      <Checkbox
                        id="checkValue"
                        name="checkValue"
                        color={theme.colors.green}
                        value={result.isChecked}
                        checked={!!result.isChecked}
                        style={{ ...CheckboxStyles }}
                        disabled={result.isDisabled}
                      />
                      {/* Artist item */}
                      <ItemWrapper isDisabled={result.isDisabled}>
                        {result.name}
                      </ItemWrapper>

                      {/* Delete artist */}
                      {result.isDeleteAvailable && (
                        <DeleteIconWrapper
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArtistRemove(result.name);
                          }}
                        />
                      )}
                    </RowItemWrapper>
                  </ComboboxOptionWrapper>
                ))}
              </Tonality.ComboboxList>
            ) : (
              // Get create artist when no result found
              <NoResultMessage>{getCreateArtist()}</NoResultMessage>
            )}
          </Tonality.ComboboxPopover>
        )}

        {/* Inline error message */}
        {validationError && (
          <InlineErrorMessage>
            {t('MULTI_ASSET_ARTIST_INLINE_ERROR')}
          </InlineErrorMessage>
        )}
      </Tonality.Combobox>

      {/* Artist tag list */}
      <ArtistTag
        artist={tracks}
        displayMultiSelection={doTracksHaveDifferentArtistNames(tracks)}
        tagRemove={handleTabRemove}
        multiTagRemove={handleMultiTagRemove}
      />
    </>
  );
};

export default ArtistSelection;
